/**
 * DataTable Component
 * AJAX-powered table with filtering, pagination, and sorting
 */

import { api } from '../utils/api.js';
import { updateUrlParams } from '../utils/helpers.js';

/**
 * DataTable class for managing AJAX tables
 */
export class DataTable {
  /**
   * Create a DataTable instance
   * @param {object} options - Configuration options
   * @param {string} options.containerId - ID of the table container
   * @param {string} options.endpoint - API endpoint URL
   * @param {string} options.tableSelector - Selector for the table body to update
   * @param {string} options.paginationSelector - Selector for pagination container
   * @param {string} options.statsSelector - Selector for stats/count display
   * @param {boolean} options.updateUrl - Update browser URL on filter change (default: true)
   * @param {Function} options.onFetch - Callback before fetching
   * @param {Function} options.onSuccess - Callback after successful fetch
   * @param {Function} options.onError - Callback on fetch error
   * @param {Function} options.renderRow - Custom row render function (optional)
   */
  constructor(options) {
    this.containerId = options.containerId;
    this.endpoint = options.endpoint;
    this.tableSelector = options.tableSelector || '.table-body';
    this.paginationSelector = options.paginationSelector || '.pagination, nav[aria-label]';
    this.statsSelector = options.statsSelector;
    this.updateUrl = options.updateUrl !== false;
    this.onFetch = options.onFetch;
    this.onSuccess = options.onSuccess;
    this.onError = options.onError;
    this.renderRow = options.renderRow;

    // State
    this.filters = {};
    this.page = 1;
    this.sort = null;
    this.order = 'asc';
    this.isLoading = false;

    // Elements
    this.container = document.getElementById(this.containerId);
    this.tableBody = this.container?.querySelector(this.tableSelector);
    this.pagination = this.container?.querySelector(this.paginationSelector);

    // Bind methods
    this.fetch = this.fetch.bind(this);
    this.setFilters = this.setFilters.bind(this);
    this.setPage = this.setPage.bind(this);
    this.setSort = this.setSort.bind(this);
    this.refresh = this.refresh.bind(this);

    // Initialize from URL
    this.initializeFromUrl();
  }

  /**
   * Initialize state from URL parameters
   */
  initializeFromUrl() {
    const params = new URLSearchParams(window.location.search);

    params.forEach((value, key) => {
      if (key === 'page') {
        this.page = parseInt(value, 10) || 1;
      } else if (key === 'sort') {
        this.sort = value;
      } else if (key === 'order') {
        this.order = value;
      } else {
        this.filters[key] = value;
      }
    });
  }

  /**
   * Build URL with current state
   * @returns {string} URL with query parameters
   */
  buildUrl() {
    const url = new URL(this.endpoint, window.location.origin);

    // Add filters
    Object.entries(this.filters).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        url.searchParams.set(key, value);
      }
    });

    // Add pagination
    if (this.page > 1) {
      url.searchParams.set('page', this.page);
    }

    // Add sorting
    if (this.sort) {
      url.searchParams.set('sort', this.sort);
      url.searchParams.set('order', this.order);
    }

    return url.toString();
  }

  /**
   * Fetch data from server
   */
  async fetch() {
    if (this.isLoading) return;

    this.isLoading = true;

    // Show loading state
    if (this.tableBody) {
      this.tableBody.style.opacity = '0.5';
    }

    // Callback
    if (this.onFetch) {
      this.onFetch(this);
    }

    const url = this.buildUrl();

    try {
      const html = await api(url, { method: 'GET' }, { showErrorToast: false });

      // Parse response
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Update table body
      const newTable = doc.querySelector(this.tableSelector);
      if (newTable && this.tableBody) {
        this.tableBody.innerHTML = newTable.innerHTML;
        this.tableBody.style.opacity = '1';
      }

      // Update pagination
      const newPagination = doc.querySelector(this.paginationSelector);
      if (newPagination && this.pagination) {
        this.pagination.innerHTML = newPagination.innerHTML;
        this.bindPaginationEvents();
      }

      // Update stats
      if (this.statsSelector) {
        const newStats = doc.querySelector(this.statsSelector);
        const currentStats = document.querySelector(this.statsSelector);
        if (newStats && currentStats) {
          currentStats.innerHTML = newStats.innerHTML;
        }
      }

      // Update browser URL
      if (this.updateUrl) {
        updateUrlParams(
          {
            ...this.filters,
            page: this.page > 1 ? this.page : null,
            sort: this.sort,
            order: this.sort ? this.order : null,
          },
          true
        );
      }

      if (typeof window.reinitFlowbiteWidgets === 'function') {
        window.reinitFlowbiteWidgets(this.container || undefined);
      }

      // Success callback
      if (this.onSuccess) {
        this.onSuccess(doc, this);
      }
    } catch (error) {
      console.error('DataTable fetch error:', error);

      if (this.tableBody) {
        this.tableBody.style.opacity = '1';
      }

      // Error callback
      if (this.onError) {
        this.onError(error, this);
      } else {
        // Fallback: reload page
        window.location.href = this.buildUrl();
      }
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Bind pagination click events
   */
  bindPaginationEvents() {
    if (!this.pagination) return;

    this.pagination.querySelectorAll('a[href*="page="]').forEach((link) => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const url = new URL(link.href);
        const page = url.searchParams.get('page');
        if (page) {
          this.setPage(parseInt(page, 10));
        }
      });
    });
  }

  /**
   * Set filters and fetch
   * @param {object} filters - Filter key-value pairs
   * @param {boolean} resetPage - Reset to page 1 (default: true)
   */
  setFilters(filters, resetPage = true) {
    this.filters = { ...this.filters, ...filters };

    // Remove empty filters
    Object.keys(this.filters).forEach((key) => {
      if (this.filters[key] === null || this.filters[key] === undefined || this.filters[key] === '') {
        delete this.filters[key];
      }
    });

    if (resetPage) {
      this.page = 1;
    }

    this.fetch();
  }

  /**
   * Clear all filters and fetch
   */
  clearFilters() {
    this.filters = {};
    this.page = 1;
    this.fetch();
  }

  /**
   * Set page and fetch
   * @param {number} page - Page number
   */
  setPage(page) {
    this.page = page;
    this.fetch();
  }

  /**
   * Set sorting and fetch
   * @param {string} column - Column to sort by
   * @param {string} order - Sort order ('asc' or 'desc')
   */
  setSort(column, order = null) {
    // Toggle order if same column
    if (this.sort === column && order === null) {
      this.order = this.order === 'asc' ? 'desc' : 'asc';
    } else {
      this.sort = column;
      this.order = order || 'asc';
    }

    this.page = 1; // Reset to first page when sorting
    this.fetch();
  }

  /**
   * Refresh with current state
   */
  refresh() {
    this.fetch();
  }

  /**
   * Get current state
   * @returns {object} Current filters, page, and sort state
   */
  getState() {
    return {
      filters: { ...this.filters },
      page: this.page,
      sort: this.sort,
      order: this.order,
    };
  }

  /**
   * Destroy the data table instance
   */
  destroy() {
    this.container = null;
    this.tableBody = null;
    this.pagination = null;
  }
}

// Expose globally for backward compatibility
window.DataTable = DataTable;

export default DataTable;
